//hooks
import { useMemo, MouseEvent, ChangeEvent, Fragment } from "react";
import {
  useTheme,
  Stack,
  Box,
  Paper,
  TablePagination,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from "@mui/material";

//types
import { TableCellProps } from "@mui/material/TableCell";
import {
  RowsPerPageOption,
  TableColumnControlGroupValue,
} from "../modules/charts/types";

//functions
import { styled } from "@mui/material/styles";

//components
import Loader from "./Loader";
import TablePaginationActions from "./TablePaginationActions";

const rowsPerPageOptions: RowsPerPageOption[] = [5, 10, 50, 100];

type StyledTableCellProps = TableCellProps & {
  textalign?: "left" | "center" | "right";
};

const StyledTableCell = styled(TableCell)<StyledTableCellProps>(
  ({ theme, textalign = "left" }) => ({
    border: `0.5px solid #ECECEC`,
    fontSize: "16px",
    fontWeight: 500,
    color: "#343A40",
    textAlign: textalign,
    minWidth: "120px",
  }),
);

const StyledTableHeadCell = styled(TableCell)<StyledTableCellProps>(
  ({ theme, textalign = "center" }) => ({
    borderRight: `0.5px solid ${theme.palette.divider}`,
    fontSize: "16px",
    fontWeight: 500,
    color: "#343A40",
    backgroundColor: "#ECEFF3",
    textAlign: textalign,
    // minWidth: "120px",
  }),
);

interface TablePaginationProps {
  page: number;
  rowsPerPage: number;
  totalPages: number;
  handleChangePage: (
    event: MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => void;
  handleChangeRowsPerPage: (
    event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => void;
}

interface ICustomTable {
  readonly rows?: any[] | null;
  readonly columns: TableColumnControlGroupValue[];
  readonly loading: boolean;
  readonly paginationProps?: TablePaginationProps;
}

export default function CustomTable({
  rows,
  columns,
  loading,
  paginationProps,
}: ICustomTable) {
  const theme = useTheme();

  // Avoid a layout jump when reaching the last page with empty rows.
  const emptyRows =
    paginationProps && paginationProps.page > 0
      ? Math.max(
          0,
          (1 + paginationProps.page) * paginationProps.rowsPerPage -
            paginationProps.rowsPerPage,
        )
      : 0;

  const processCalculatedFields = (
    rows: any[],
    columns: TableColumnControlGroupValue[],
  ) => {
    rows.forEach((row, idx, _rows) => {
      columns.forEach(({ column, calculate }) => {
        if (!calculate) return;
        _rows[idx][column] = calculate(row);
      });
    });
  };

  const hasSameParentValues = (
    currentRow: any,
    nextRow: any,
    parentColumns: string[] | null,
  ) => {
    if (!parentColumns) return false;
    if (parentColumns.length === 0) return true;
    return parentColumns.every(
      (parentField: string) => currentRow[parentField] === nextRow[parentField],
    );
  };

  const updateRowSpan = (currentRow: any, column: string, span: number) => {
    if (!("rowSpan" in currentRow)) currentRow.rowSpan = {};
    if (!(column in currentRow.rowSpan)) currentRow.rowSpan[column] = 1;
    currentRow.rowSpan[column] = span;
  };

  const markColumnAsIgnored = (row: any, column: string) => {
    if (!("ignoreCols" in row)) row.ignoreCols = [];
    row.ignoreCols.push(column);
  };

  const processColumnSpan = (
    row: any,
    nextRow: any,
    column: string,
    parentColumns: string[] | null,
    idx: number,
    nextIdx: number,
  ) => {
    if (
      row[column] === nextRow[column] &&
      hasSameParentValues(row, nextRow, parentColumns)
    ) {
      updateRowSpan(row, column, nextIdx - idx + 1);
      markColumnAsIgnored(nextRow, column);
      return true;
    }
    return false;
  };

  const processRowForColumn = (
    row: any,
    idx: number,
    _rows: any[],
    column: string,
    parentColumns: string[] | null,
  ) => {
    if ("ignoreCols" in row && row.ignoreCols.includes(column)) return;

    let nextIdx = idx < _rows.length - 1 ? idx + 1 : null;

    try {
      while (
        nextIdx &&
        processColumnSpan(
          row,
          _rows[nextIdx],
          column,
          parentColumns,
          idx,
          nextIdx,
        )
      ) {
        nextIdx = nextIdx < _rows.length - 1 ? nextIdx + 1 : null;
      }
    } catch (e) {
      console.error(e);
    }
  };

  const processRowSpans = (
    rows: any[],
    columns: TableColumnControlGroupValue[],
  ) => {
    rows.forEach((row, idx, _rows) => {
      columns.forEach(({ column, parentColumns }) => {
        processRowForColumn(row, idx, _rows, column, parentColumns);
      });
    });
  };

  const data = useMemo<any[]>(() => {
    if (!rows) return [];

    processCalculatedFields(rows, columns);
    processRowSpans(rows, columns);

    return rows;
  }, [rows, columns]);

  return (
    <Stack direction="column" sx={{ width: "100%", height: `100%` }}>
      <Box
        sx={{
          position: "relative",
          flex: 1,
          minHeight: 0,
          width: `calc(100% - ${theme.spacing(4)})`,
          maxHeight: `calc(100% - ${theme.spacing(17)})`,
        }}
      >
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{
            width: "100%",
            height: "100%",
            boxShadow: "none",
            borderRadius: "5px",
            border: `1px solid ${theme.palette.divider}`,
          }}
        >
        <Table className={"table"} aria-label="project table" stickyHeader>
          <TableHead>
            <TableRow>
              {columns.map(
                (column) =>
                  !column.hidden && (
                    <StyledTableHeadCell
                      key={column.column}
                      sx={
                        column.getHeadStyles
                          ? column.getHeadStyles(theme)
                          : { minWidth: column.width }
                      }
                    >
                      {column.label}
                    </StyledTableHeadCell>
                  ),
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((row: any, index: number) => (
              <TableRow key={`${index}-${row[columns[0]?.column]}`}>
                {columns.map(
                  ({ column, hidden, getCellStyles, formatValue }) =>
                    "ignoreCols" in row && row.ignoreCols.includes(column) ? (
                      <Fragment key={column}></Fragment>
                    ) : (
                      !hidden && (
                        <StyledTableCell
                          key={column}
                          textalign="center"
                          rowSpan={
                            row.rowSpan && column in row.rowSpan
                              ? row.rowSpan[column]
                              : 1
                          }
                          sx={
                            getCellStyles
                              ? getCellStyles(row, row[column], theme)
                              : {}
                          }
                        >
                          {formatValue
                            ? formatValue(row[column])
                            : (row[column] ?? "")}
                        </StyledTableCell>
                      )
                    ),
                )}
              </TableRow>
            ))}
            {rows && rows.length === 0 && (
              <TableRow>
                <StyledTableCell colSpan={columns.length} textalign="center">
                  {"No results to display"}
                </StyledTableCell>
              </TableRow>
            )}
            {paginationProps && emptyRows > 0 && (
              <TableRow>
                <StyledTableCell colSpan={columns.length} />
              </TableRow>
            )}
          </TableBody>
        </Table>
        </TableContainer>
        {loading && (
          <Box
            sx={{
              position: "absolute",
              inset: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "rgba(255, 255, 255, 0.7)",
            }}
          >
            <Loader />
          </Box>
        )}
      </Box>
      {paginationProps && (
        <TablePagination
          rowsPerPageOptions={rowsPerPageOptions}
          colSpan={3}
          count={paginationProps.totalPages}
          rowsPerPage={paginationProps.rowsPerPage}
          page={paginationProps.page}
          slotProps={{
            select: {
              inputProps: {
                "aria-label": "rows per page",
              },
              native: false,
            },
          }}
          onPageChange={paginationProps.handleChangePage}
          onRowsPerPageChange={paginationProps.handleChangeRowsPerPage}
          ActionsComponent={TablePaginationActions}
          component={Box}
          sx={{
            position: "absolute",
            bottom: theme.spacing(2),
            right: theme.spacing(2),
          }}
        />
      )}
      <Box height={theme.spacing(8)}></Box>
    </Stack>
  );
}
